import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from '@legendapp/state/react';
import { cartStoreAPI } from '../stores/cart-store.public-api';
import { cartItemReferenceStoreAPI } from '../stores/cart-item-reference-store.public-api';
import { getLastUserActionTimestamp } from '../stores/cart-item-reference.store';
import { calculateSelectedReferencesPrices } from '../helpers/cart-item.helpers';
import { type CartPrices, type CurrencyEnum, useFastCartTotalsLazyQuery } from '../types/front.graphql';
import type { CartItemReference } from '../models/types/cart-item-reference';
import type { CartItemLocalInterface } from '../models/cart.model';

/**
 * Price confidence levels for UI indicators.
 * - 'server': Accurate prices with all discounts + shipping (server confirmed)
 * - 'local-estimated': Estimated prices from local calculation (no cart-level discounts)
 * - 'server-stale': Showing last known good server prices (current fetch pending/failed)
 */
export type PriceConfidenceLevel = 'server' | 'local-estimated' | 'server-stale';

/**
 * Map FastCartTotals response to CartPrices format.
 *
 * Magento field semantics (from FastCartTotals.php resolver):
 *   - subtotal              = raw subtotal of all items (no discounts, no tax adjustment)
 *   - subtotal_with_discount = subtotal AFTER discount rules are applied
 *   - grand_total           = final total (subtotal − discounts + shipping + tax)
 *   - discount_amount       = absolute total discount (cart rules + voucher)
 *   - tax_amount            = total tax
 *
 * Mapping rationale:
 *   - subtotal_excluding_tax  ← subtotal (raw item totals, tax excluded conceptually)
 *   - subtotal_including_tax  ← subtotal + tax_amount (VN prices already include VAT
 *                                so in practice subtotal ≈ subtotal_including_tax)
 *   - subtotal_with_discount_excluding_tax ← subtotal_with_discount
 *   - grand_total             ← grand_total
 */
const mapFastCartTotalsToCartPrices = (fastTotals: {
    subtotal?: number | null;
    subtotal_with_discount?: number | null;
    grand_total?: number | null;
    discount_amount?: number | null;
    coupon_code?: string | null;
    tax_amount?: number | null;
    currency_code?: string | null;
}): CartPrices => {
    const currency = (fastTotals.currency_code as CurrencyEnum) ?? 'VND';
    const subtotal = fastTotals.subtotal ?? 0;
    const taxAmount = fastTotals.tax_amount ?? 0;

    return {
        __typename: 'CartPrices',
        subtotal_excluding_tax: {
            __typename: 'Money',
            value: subtotal,
            currency,
        },
        subtotal_including_tax: {
            __typename: 'Money',
            // subtotal + tax: For VN (0% tax), this equals subtotal.
            // Previously this was incorrectly mapped to subtotal_with_discount.
            value: subtotal + taxAmount,
            currency,
        },
        subtotal_with_discount_excluding_tax: {
            __typename: 'Money',
            value: fastTotals.subtotal_with_discount ?? 0,
            currency,
        },
        grand_total: {
            __typename: 'Money',
            value: fastTotals.grand_total ?? 0,
            currency,
        },
        discounts: fastTotals.discount_amount
            ? [
                  {
                      __typename: 'Discount',
                      label: fastTotals.coupon_code ?? 'Discount',
                      amount: {
                          __typename: 'Money',
                          // Normalize to positive: Magento FastCartTotals returns negative
                          // discount_amount (e.g., -100000), but standard CartPrices.discounts
                          // convention uses positive values.
                          value: Math.abs(fastTotals.discount_amount),
                          currency,
                      },
                  },
              ]
            : null,
        applied_taxes: fastTotals.tax_amount
            ? [
                  {
                      __typename: 'CartTaxItem',
                      label: 'Tax',
                      amount: {
                          __typename: 'Money',
                          value: fastTotals.tax_amount,
                          currency,
                      },
                  },
              ]
            : null,
    };
};

const SMOOTH_ANIMATION_DURATION_MS = 300;
// TODO-4.5: Increased from 100 to 500 for VND currency.
// 100 VND is too small for Vietnamese Dong. Meaningful price changes are typically 1000+ VND.
const SMOOTH_MIN_DIFFERENCE = 500;

// TODO-4.3: Reduced from 4000ms to 2000ms for better UX.
// With 800ms debounce + typical 300ms network latency, 2s is sufficient.
const SYNC_WAIT_FALLBACK_MS = 2000;

// TODO-3.5: Max retry count for FastCartTotals when server data is not computed
const MAX_SERVER_RETRY_COUNT = 2;
const SERVER_RETRY_DELAY_MS = 2000;

// TODO-7.7: Consecutive fetch failure threshold for error state
const CONSECUTIVE_FAILURE_THRESHOLD = 3;

/**
 * Smoothly animate price transitions from local to server prices.
 * Snaps instantly on user selection changes (fingerprint change).
 * Animates smoothly when server prices arrive for the same selection.
 */
function useSmoothPrices(targetPrices: CartPrices, fingerprint: string): CartPrices {
    const grandTotal = targetPrices.grand_total?.value ?? 0;
    const subtotalExTax = targetPrices.subtotal_excluding_tax?.value ?? 0;
    const subtotalInTax = targetPrices.subtotal_including_tax?.value ?? 0;
    const subtotalDiscount = targetPrices.subtotal_with_discount_excluding_tax?.value ?? 0;

    const [displayed, setDisplayed] = useState({
        grandTotal,
        subtotalExTax,
        subtotalInTax,
        subtotalDiscount,
    });
    const displayedRef = useRef(displayed);
    const animRef = useRef<number | null>(null);
    const prevFingerprintRef = useRef(fingerprint);

    useEffect(() => {
        const fpChanged = prevFingerprintRef.current !== fingerprint;
        prevFingerprintRef.current = fingerprint;

        if (animRef.current != null) {
            cancelAnimationFrame(animRef.current);
            animRef.current = null;
        }

        const cur = displayedRef.current;
        const maxDiff = Math.max(
            Math.abs(cur.grandTotal - grandTotal),
            Math.abs(cur.subtotalExTax - subtotalExTax),
            Math.abs(cur.subtotalDiscount - subtotalDiscount)
        );

        // Snap instantly on user action (fingerprint changed) or tiny difference
        if (fpChanged || maxDiff < SMOOTH_MIN_DIFFERENCE) {
            const snapped = { grandTotal, subtotalExTax, subtotalInTax, subtotalDiscount };
            displayedRef.current = snapped;
            setDisplayed(snapped);
            return;
        }

        // Animate: server prices arrived for same selection
        const start = { ...cur };
        const t0 = Date.now();

        const tick = () => {
            const elapsed = Date.now() - t0;
            const p = Math.min(elapsed / SMOOTH_ANIMATION_DURATION_MS, 1);
            const e = 1 - Math.pow(1 - p, 3); // ease-out cubic

            const next = {
                grandTotal: Math.round(start.grandTotal + (grandTotal - start.grandTotal) * e),
                subtotalExTax: Math.round(start.subtotalExTax + (subtotalExTax - start.subtotalExTax) * e),
                subtotalInTax: Math.round(start.subtotalInTax + (subtotalInTax - start.subtotalInTax) * e),
                subtotalDiscount: Math.round(start.subtotalDiscount + (subtotalDiscount - start.subtotalDiscount) * e),
            };

            displayedRef.current = next;
            setDisplayed(next);

            if (p < 1) {
                animRef.current = requestAnimationFrame(tick);
            }
        };

        animRef.current = requestAnimationFrame(tick);

        return () => {
            if (animRef.current != null) {
                cancelAnimationFrame(animRef.current);
            }
        };
    }, [grandTotal, subtotalExTax, subtotalInTax, subtotalDiscount, fingerprint]);

    return useMemo(
        (): CartPrices => ({
            ...targetPrices,
            grand_total: targetPrices.grand_total
                ? { ...targetPrices.grand_total, value: displayed.grandTotal }
                : targetPrices.grand_total,
            subtotal_excluding_tax: targetPrices.subtotal_excluding_tax
                ? { ...targetPrices.subtotal_excluding_tax, value: displayed.subtotalExTax }
                : targetPrices.subtotal_excluding_tax,
            subtotal_including_tax: targetPrices.subtotal_including_tax
                ? { ...targetPrices.subtotal_including_tax, value: displayed.subtotalInTax }
                : targetPrices.subtotal_including_tax,
            subtotal_with_discount_excluding_tax: targetPrices.subtotal_with_discount_excluding_tax
                ? { ...targetPrices.subtotal_with_discount_excluding_tax, value: displayed.subtotalDiscount }
                : targetPrices.subtotal_with_discount_excluding_tax,
        }),
        [targetPrices, displayed]
    );
}

/**
 * Hybrid cart prices hook - Shows local calculation immediately,
 * then silently updates with server prices when available
 *
 * PORTED FROM MOBILE:
 * - Uses fingerprint-based stale detection for instant UI updates on check/uncheck
 * - Uses FastCartTotals (lightweight query) instead of full cart query
 * - Now calculates prices from CartItemReference[] instead of CartItemInterface[]
 * - Uses isSelected flag from references instead of selectedItemIds array
 *
 * IMPORTANT LOGIC:
 * - Local calculation EXCLUDES cart-level discounts (coupons, promotions)
 * - Only item-level discounts are calculated locally (these are accurate)
 * - Cart-level discounts require server calculation because:
 *   1. They apply to ENTIRE cart, not just selected items
 *   2. Proportional distribution is complex and may differ from server rules
 *   3. Promotion rules (buy X get Y) cannot be replicated locally
 * - Server prices are ALWAYS fetched for accuracy
 * - Grand total includes shipping, which is ONLY available from server
 *
 * @returns Object containing prices (local first, then server) and metadata
 */
export const useHybridCartPrices = () => {
    const cartId = useSelector(() => cartStoreAPI.state.getCartIdObservable().get());

    const allReferences = useSelector(() => cartItemReferenceStoreAPI.state.getItemsObservable().get());

    // TODO-IND-3.1: Generation counter — monotonically increasing integer.
    // Incremented on every state change. Replaces string fingerprint for stale detection.
    // O(1) compared to fingerprint's O(n*log(n)) (map + sort + join).
    const stateGenerationRef = useRef(0);

    // Selection fingerprint — changes whenever checked items or quantities change.
    // Used to detect when server data becomes stale after a check/uncheck action.
    const selectionFingerprint = useMemo(() => {
        // Increment generation on every recomputation
        stateGenerationRef.current += 1;

        const selected = (allReferences ?? []).filter(
            (ref: CartItemReference | null | undefined) => ref?.isSelected === true
        );
        if (selected.length === 0) return '';
        return selected
            .map((ref: CartItemReference | null | undefined) => `${ref?.cartItemReferenceId}:${ref?.qty}`)
            .sort()
            .join(',');
    }, [allReferences]);

    // Tracks which selection fingerprint the current server data was fetched for.
    const [serverDataFingerprint, setServerDataFingerprint] = useState<string>('');

    // TODO-4.1: Fetch generation counter to prevent stale server responses.
    // Each fetch captures the current generation. Only responses matching the
    // latest generation are accepted, preventing out-of-order response overwrites.
    const fetchGenerationRef = useRef(0);

    // TODO-4.2: Keep last known server discount info for display during local price window.
    // When local prices are shown (no fresh server data), we preserve the last known
    // discount amount and coupon code so the UI doesn't flash to zero discount.
    const lastKnownServerDiscountRef = useRef<{
        couponCode: string | null;
        discountAmount: number;
    } | null>(null);

    // TODO-IND-5.1: Shadow State — last confirmed server prices as "back buffer".
    // Never cleared by fingerprint changes. Updated only when fresh server data arrives.
    const lastConfirmedServerPricesRef = useRef<CartPrices | undefined>(undefined);

    // TODO-7.7: Track consecutive fetch failures for error state
    const [consecutiveFailures, setConsecutiveFailures] = useState(0);
    const serverPriceError = consecutiveFailures >= CONSECUTIVE_FAILURE_THRESHOLD;

    // TODO-3.5: Retry counter for server data not computed
    const retryCountRef = useRef(0);

    // TODO-IND-4.1: AbortController for FastCartTotals — cancel previous in-flight request
    const abortControllerRef = useRef<AbortController | null>(null);

    // TODO-3.1: Server prices version counter — increments each time fresh server prices arrive.
    // Dependent components (coupon form, gift display) can react to version changes
    // instead of polling or duplicating FastCartTotals requests.
    const [serverPricesVersion, setServerPricesVersion] = useState(0);

    // Calculate LOCAL prices reactively from SELECTED references only
    // Auto-recalculates when references or their selection state changes
    const localPrices = useSelector(() => {
        const references = cartItemReferenceStoreAPI.state.getItemsObservable().get() ?? [];

        // Filter selected references
        const selectedReferences = references.filter(
            (ref: CartItemReference | null | undefined) => ref?.isSelected === true
        );

        if (selectedReferences.length === 0) {
            const curr = cartStoreAPI.state.getCurrencyObservable().get();
            return {
                __typename: 'CartPrices',
                subtotal_excluding_tax: { __typename: 'Money', value: 0, currency: curr },
                subtotal_including_tax: { __typename: 'Money', value: 0, currency: curr },
                subtotal_with_discount_excluding_tax: { __typename: 'Money', value: 0, currency: curr },
                grand_total: { __typename: 'Money', value: 0, currency: curr },
                discounts: null,
                applied_taxes: null,
            } as CartPrices;
        }

        const curr = cartStoreAPI.state.getCurrencyObservable().get();

        // CRITICAL: Calculate from selected references only
        // This excludes cart-level discounts (handled by server)
        return calculateSelectedReferencesPrices(
            [...references], // Cast readonly to mutable for utility
            curr ?? ('VND' as CurrencyEnum)
        );
    });

    // Lazy fetch server prices in background using FastCartTotals (lightweight query)
    const [fetchFastCartTotalsQuery, { data: fastServerData, loading: loadingServerPrices }] =
        useFastCartTotalsLazyQuery({
            fetchPolicy: 'network-only', // Always get fresh data from server
        });

    // Map FastCartTotals to CartPrices when server data is available
    const serverPricesMapped = useMemo(() => {
        if (!fastServerData?.fastCartTotals) return undefined;
        return mapFastCartTotalsToCartPrices(fastServerData.fastCartTotals);
    }, [fastServerData?.fastCartTotals]);

    // Track last fetched selection to avoid redundant fetches
    const lastFetchedKey = useRef<string>('');

    // Wrapped fetch with generation tracking, abort, and error counting
    const fetchServerPricesWithTracking = useCallback(
        (fetchCartId: string, fetchFingerprint: string, currentKey: string) => {
            // Increment fetch generation
            fetchGenerationRef.current += 1;
            const thisGeneration = fetchGenerationRef.current;

            // TODO-4.7: Capture user action timestamp at fetch start.
            // If a user action occurs AFTER this fetch starts, the response
            // is stale and must be rejected.
            const fetchStartUserActionTs = getLastUserActionTimestamp();

            // TODO-IND-4.1: Abort previous in-flight request
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }
            const controller = new AbortController();
            abortControllerRef.current = controller;

            lastFetchedKey.current = currentKey;

            fetchFastCartTotalsQuery({
                variables: { cartId: fetchCartId },
                context: {
                    fetchOptions: { signal: controller.signal },
                },
            })
                .then(result => {
                    // TODO-4.1: Only accept if this is still the latest generation
                    if (thisGeneration !== fetchGenerationRef.current) {
                        return; // Stale response — a newer fetch was issued
                    }

                    // TODO-4.7: Reject if user performed an action after this fetch started.
                    // The server data reflects the cart state BEFORE the user action.
                    if (getLastUserActionTimestamp() > fetchStartUserActionTs) {
                        console.warn(
                            '[useHybridCartPrices] Rejecting server response — user action occurred after fetch started'
                        );
                        // Reset lastFetchedKey so the next effect re-run triggers a fresh fetch
                        lastFetchedKey.current = '';
                        return;
                    }

                    setServerDataFingerprint(fetchFingerprint);
                    setConsecutiveFailures(0);
                    retryCountRef.current = 0;

                    // TODO-3.1: Notify dependents that fresh server prices arrived
                    setServerPricesVersion(prev => prev + 1);

                    // TODO-4.2: Update last known discount info
                    const totals = result?.data?.fastCartTotals;
                    if (totals) {
                        lastKnownServerDiscountRef.current = {
                            couponCode: totals.coupon_code ?? null,
                            discountAmount: totals.discount_amount ?? 0,
                        };

                        // Sync to cartStore$.fastCartTotals so checkout reads pre-populated data
                        cartStoreAPI.actions.setFastCartTotals(totals);
                    }

                    // TODO-IND-5.1: Update last confirmed server prices (back buffer)
                    if (result?.data?.fastCartTotals) {
                        lastConfirmedServerPricesRef.current = mapFastCartTotalsToCartPrices(
                            result.data.fastCartTotals
                        );
                    }
                })
                .catch(error => {
                    // Ignore abort errors
                    if (error?.name === 'AbortError') return;

                    setConsecutiveFailures(prev => prev + 1);
                    console.error('[useHybridCartPrices] Error fetching server cart prices:', error);
                });
        },
        [fetchFastCartTotalsQuery]
    );

    // Fetch server prices silently in background when conditions are met
    useEffect(() => {
        if (!cartId) {
            return;
        }

        // Get selected references
        const selectedReferences = allReferences.filter(
            (ref: CartItemReference | null | undefined) => ref?.isSelected === true
        );

        if (selectedReferences.length === 0) {
            return;
        }

        // Create stable key based on selected reference IDs AND quantities
        // Must include qty because changing quantity should trigger server price refetch
        const selectedReferenceData = selectedReferences
            .map(
                (ref: CartItemReference | null | undefined) =>
                    `${ref?.cartItemReferenceId}:${ref?.qty}:${ref?.isSelected ? '1' : '0'}`
            )
            .sort()
            .join(',');
        const currentKey = `${cartId}-${selectedReferenceData}`;

        // RACE CONDITION FIX: Check if local changes are pending sync to server.
        // If so, the server still has old cart data. Fetching FastCartTotals now
        // would return stale totals that get incorrectly accepted (fingerprint match).
        // After sync completes, applyStoreMutations updates the store → allReferences
        // changes → this effect re-runs → hasPendingChanges is false → fetch proceeds.
        const { hasPendingChanges } = cartItemReferenceStoreAPI.computed.getSyncStatus();

        if (hasPendingChanges) {
            // Invalidate current server data so local prices are used immediately
            setServerDataFingerprint('');

            // Reset lastFetchedKey so that when sync completes and the effect
            // re-runs, it will refetch even if the currentKey hasn't changed.
            lastFetchedKey.current = '';

            // Fallback: if the sync store update doesn't re-trigger this effect
            // (e.g., reconciled items identical to local), fetch after a safe delay.
            const fetchFingerprint = selectionFingerprint;
            const fallbackTimer = setTimeout(() => {
                // Re-check: only fetch if sync has actually completed
                const syncStatus = cartItemReferenceStoreAPI.computed.getSyncStatus();
                if (!syncStatus.hasPendingChanges) {
                    fetchServerPricesWithTracking(cartId, fetchFingerprint, currentKey);
                }
            }, SYNC_WAIT_FALLBACK_MS);

            return () => clearTimeout(fallbackTimer);
        }

        // No pending changes — safe to fetch server prices
        if (currentKey !== lastFetchedKey.current) {
            fetchServerPricesWithTracking(cartId, selectionFingerprint, currentKey);
        }
    }, [cartId, allReferences, fetchServerPricesWithTracking, selectionFingerprint]);

    // Use mapped SERVER prices when available, otherwise use LOCAL prices
    const serverPrices = serverPricesMapped;

    // Server prices are fresh only when they were fetched for the CURRENT selection.
    // On check/uncheck the fingerprint changes instantly, making server data stale
    // so local prices are used without waiting for the network round-trip.
    const isServerDataFreshForSelection = selectionFingerprint !== '' && serverDataFingerprint === selectionFingerprint;

    // Calculate selected references metadata reactively
    // (Computed before isServerDataComputed so items_count validation can use it)
    const selectedReferencesData = useSelector(() => {
        const references = cartItemReferenceStoreAPI.state.getItemsObservable().get() ?? [];
        const selected = references.filter((ref: CartItemReference | null | undefined) => ref?.isSelected === true);
        const quantity = selected.reduce(
            (sum: number, ref: CartItemReference | null | undefined) => sum + (ref?.qty ?? 0),
            0
        );

        // Extract cart items from selected references
        const cartItems = selected
            .map((ref: CartItemReference | null | undefined) => ref?.cartItem)
            .filter((item): item is CartItemLocalInterface => !!item);

        return {
            references: selected,
            cartItems,
            count: selected.length,
            quantity,
            hasSelected: selected.length > 0,
        };
    });

    // TODO-1.5 + TODO-3.4: Enhanced server data validation
    const isServerDataComputed = useMemo(() => {
        const raw = fastServerData?.fastCartTotals;
        if (!raw) return false;
        if (raw.grand_total === null || raw.grand_total === undefined) return false;

        // TODO-1.5: Also validate subtotal is present when items exist
        const selectedCount = selectedReferencesData.count;
        if (selectedCount > 0 && (raw.subtotal === null || raw.subtotal === undefined)) {
            console.warn('[useHybridCartPrices] Server subtotal is null with selected items — treating as stale');
            return false;
        }

        // TODO-3.4: When discount_amount is non-zero, subtotal_with_discount must also be computed.
        // If server returns grand_total but hasn't finished computing discounts, we get wrong values.
        // Note: Magento may return discount_amount as negative (e.g., -100000).
        if (
            raw.discount_amount != null &&
            Math.abs(raw.discount_amount) > 0 &&
            (raw.subtotal_with_discount === null || raw.subtotal_with_discount === undefined)
        ) {
            console.warn(
                '[useHybridCartPrices] Server has discount but subtotal_with_discount is null — treating as stale'
            );
            return false;
        }

        // Defensive check: server items_count vs local selected count
        if (raw.items_count != null && selectedCount > 0 && raw.items_count < selectedCount) {
            console.warn('[useHybridCartPrices] Server items_count lower than expected — treating as stale', {
                serverItemsCount: raw.items_count,
                localSelectedCount: selectedCount,
            });
            return false;
        }

        return true;
    }, [fastServerData?.fastCartTotals, selectedReferencesData.count]);

    // TODO-3.5: Auto-retry when server data is not computed (collect_totals pending)
    useEffect(() => {
        if (!cartId || !isServerDataFreshForSelection || isServerDataComputed) {
            retryCountRef.current = 0;
            return;
        }

        // Server data is fresh but NOT computed — collect_totals may be pending
        if (retryCountRef.current >= MAX_SERVER_RETRY_COUNT) {
            return; // Max retries reached
        }

        const retryTimer = setTimeout(() => {
            retryCountRef.current += 1;
            // Force refetch by resetting the last fetched key
            lastFetchedKey.current = '';
            // Trigger re-fetch via state change (increment generation)
            fetchGenerationRef.current += 1;
            fetchServerPricesWithTracking(cartId, selectionFingerprint, `retry-${retryCountRef.current}`);
        }, SERVER_RETRY_DELAY_MS);

        return () => clearTimeout(retryTimer);
    }, [
        cartId,
        isServerDataFreshForSelection,
        isServerDataComputed,
        selectionFingerprint,
        fetchServerPricesWithTracking,
    ]);

    // Compute finalPrices reactively based on server data availability
    const finalPrices: CartPrices = (() => {
        if (serverPrices && !loadingServerPrices && isServerDataFreshForSelection && isServerDataComputed) {
            return serverPrices;
        }

        // TODO-IND-5.1: When local prices are active, apply last known server discounts
        // from the "back buffer" to produce a better estimated price.
        // This prevents the jarring flash where discounts disappear during local display.
        if (
            lastConfirmedServerPricesRef.current &&
            lastKnownServerDiscountRef.current &&
            lastKnownServerDiscountRef.current.discountAmount > 0
        ) {
            const localSubtotal = localPrices.subtotal_excluding_tax?.value ?? 0;
            const lastDiscount = lastKnownServerDiscountRef.current.discountAmount;
            const currency = localPrices.grand_total?.currency ?? ('VND' as CurrencyEnum);

            // Only apply last known discount if local subtotal is close to what server saw
            // (prevents applying old discount to vastly different cart composition)
            const lastServerSubtotal = lastConfirmedServerPricesRef.current.subtotal_excluding_tax?.value ?? 0;
            const subtotalDiffPct =
                lastServerSubtotal > 0 ? Math.abs(localSubtotal - lastServerSubtotal) / lastServerSubtotal : 1;

            if (subtotalDiffPct < 0.3) {
                // Within 30% of last server subtotal
                return {
                    ...localPrices,
                    grand_total: {
                        __typename: 'Money',
                        value: Math.max(0, localSubtotal - lastDiscount),
                        currency,
                    },
                    subtotal_with_discount_excluding_tax: {
                        __typename: 'Money',
                        value: Math.max(0, localSubtotal - lastDiscount),
                        currency,
                    },
                    discounts: [
                        {
                            __typename: 'Discount',
                            label: lastKnownServerDiscountRef.current.couponCode ?? 'Discount',
                            amount: {
                                __typename: 'Money',
                                value: lastDiscount,
                                currency,
                            },
                        },
                    ],
                };
            }
        }

        return localPrices;
    })();

    // Apply smooth transition
    const smoothPrices = useSmoothPrices(finalPrices, selectionFingerprint);

    // Get currency reactively using OBSERVABLE
    const currency = useSelector(() => cartStoreAPI.state.getCurrencyObservable().get());

    // Determine if prices are from server or local
    const isServerPrices = !!serverPricesMapped && isServerDataFreshForSelection && isServerDataComputed;

    // TODO-1.2: Price confidence level
    const priceConfidenceLevel: PriceConfidenceLevel = (() => {
        if (isServerPrices) return 'server';
        if (
            lastConfirmedServerPricesRef.current &&
            lastKnownServerDiscountRef.current &&
            lastKnownServerDiscountRef.current.discountAmount > 0
        ) {
            return 'server-stale';
        }
        return 'local-estimated';
    })();

    const shippingFee = useMemo(() => {
        const shippingAmount = fastServerData?.fastCartTotals?.shipping_amount;

        if (
            !isServerDataFreshForSelection ||
            !isServerDataComputed ||
            shippingAmount === undefined ||
            shippingAmount === null
        ) {
            return undefined;
        }

        return {
            amount: shippingAmount,
            currency: (fastServerData?.fastCartTotals?.currency_code as CurrencyEnum) ?? 'VND',
        };
    }, [
        fastServerData?.fastCartTotals?.shipping_amount,
        fastServerData?.fastCartTotals?.currency_code,
        isServerDataFreshForSelection,
        isServerDataComputed,
    ]);

    // TODO-7.7: Manual retry function for persistent server price failures
    const retryServerPrices = useCallback(() => {
        if (!cartId) return;
        setConsecutiveFailures(0);
        lastFetchedKey.current = '';
        fetchServerPricesWithTracking(cartId, selectionFingerprint, `manual-retry-${Date.now()}`);
    }, [cartId, selectionFingerprint, fetchServerPricesWithTracking]);

    return {
        /**
         * Final prices - shows local immediately, then server when available.
         * TODO-4.2: During local display, applies last known server discount
         * if cart composition hasn't changed significantly.
         */
        prices: smoothPrices,

        /**
         * Selected cart item references
         */
        selectedReferences: selectedReferencesData.references,

        /**
         * Cart items from selected references
         */
        selectedCartItems: selectedReferencesData.cartItems,

        /**
         * Total quantity of selected references
         */
        totalQuantity: selectedReferencesData.quantity,

        /**
         * Number of unique selected references
         */
        selectedReferenceCount: selectedReferencesData.count,

        /**
         * Whether any references are selected
         */
        hasSelectedReferences: selectedReferencesData.hasSelected,

        /**
         * Currency being used
         */
        currency,

        /**
         * Whether current prices are from server (true) or local calculation (false)
         */
        isServerPrices,

        /**
         * Whether current prices are from local calculation
         */
        isLocalPrices: !isServerPrices,

        /**
         * TODO-1.2: Price confidence level indicator for UI.
         * - 'server': Accurate, all discounts included
         * - 'server-stale': Using last known server discount (estimated)
         * - 'local-estimated': Pure local calculation, no cart-level discounts
         */
        priceConfidenceLevel,

        /**
         * Local calculated prices (always available)
         * NOTE: Only includes item-level discounts, excludes cart-level discounts and shipping
         */
        localPrices,

        /**
         * Server prices (undefined until loaded)
         * NOTE: Includes ALL discounts (item + cart-level) and shipping
         */
        serverPrices: serverPricesMapped,
        shippingFee,

        fetchFastCartTotalsQuery,

        /**
         * Coupon code from server (from FastCartTotals).
         * Available when server prices are loaded and fresh for current selection.
         * - string: coupon code is applied
         * - null: no coupon applied (server confirmed)
         * - undefined: server data not loaded yet or stale
         */
        serverCouponCode: isServerPrices ? (fastServerData?.fastCartTotals?.coupon_code ?? null) : undefined,

        /**
         * Discount amount from server (from FastCartTotals).
         * Available when server prices are loaded and fresh for current selection.
         * - number: discount amount (0 means no discount)
         * - undefined: server data not loaded yet or stale
         */
        serverDiscountAmount: isServerPrices ? (fastServerData?.fastCartTotals?.discount_amount ?? 0) : undefined,

        /**
         * TODO-7.7: True when server price fetching has failed consecutively.
         * UI should show a warning indicator when this is true.
         */
        serverPriceError,

        /**
         * TODO-7.7: Manual retry trigger for server price fetching.
         * Call when user clicks "retry" on the price error indicator.
         */
        retryServerPrices,

        /**
         * TODO-3.1: Version counter that increments each time fresh server prices arrive.
         * Dependent components can use useEffect on this value to react to server price updates.
         */
        serverPricesVersion,
    };
};
